import { type ClientMonitoringEventTarget } from '@issuemanager/models';
import { useEventCallback } from '@mui/material';
import React, { createContext, useContext, useMemo, useState } from 'react';
import {
  useSendErrorMonitoringEvent,
  useSendPerformanceMonitoringEvent,
} from '../../features/monitoring';

interface MonitorContextProps {
  startTracking: (key: ClientMonitoringEventTarget) => void;
  stopTracking: (key: ClientMonitoringEventTarget) => void;
  reportError: (key: ClientMonitoringEventTarget) => void;
  reportErrors: () => void;
  removeTracking: (key: ClientMonitoringEventTarget) => void;
}
const MonitoringContext = createContext<MonitorContextProps>({
  startTracking: () => undefined,
  stopTracking: () => undefined,
  reportError: () => undefined,
  reportErrors: () => undefined,
  removeTracking: () => undefined,
});

type MonitoringState = Map<ClientMonitoringEventTarget, number>;
export interface MonitoringProviderProps {
  children: React.ReactNode;
}
export function MonitoringProvider({ children }: MonitoringProviderProps) {
  const sendPerformanceMonitoringEvent = useSendPerformanceMonitoringEvent();
  const sendErrorMonitoringEvent = useSendErrorMonitoringEvent();

  const [state, setState] = useState<MonitoringState>(new Map());

  const startTracking = useEventCallback((key: ClientMonitoringEventTarget) => {
    setState((prevState) => {
      const newState = new Map(prevState);
      newState.set(key, Date.now());
      return newState;
    });
  });

  const stopTracking = useEventCallback((key: ClientMonitoringEventTarget) => {
    const newState = new Map(state);
    const start = newState.get(key);
    if (start) {
      const end = Date.now();
      const duration = end - start;
      // Make query to send duration to monitoring service
      sendPerformanceMonitoringEvent.mutateAsync({
        responseTime: duration,
        target: key,
      });
      // Remove the key from the state
      removeTracking(key);
    } else {
      console.log(`No item found for key: ${key}. Cannot stop tracking.`);
    }
  });

  const reportError = useEventCallback((key: ClientMonitoringEventTarget) => {
    // Make query to send error to monitoring service
    sendErrorMonitoringEvent.mutateAsync({
      target: key,
    });
    // Remove the key from the state
    removeTracking(key);
  });

  const reportErrors = useEventCallback(() => {
    state.forEach((_, key) => {
      reportError(key as ClientMonitoringEventTarget);
    });
    setState(new Map());
  });

  const removeTracking = useEventCallback(
    (key: ClientMonitoringEventTarget) => {
      setState((prevState) => {
        const newState = new Map(prevState);
        newState.delete(key);
        return newState;
      });
    },
  );

  const value = useMemo(
    () => ({
      startTracking,
      stopTracking,
      reportError,
      reportErrors,
      removeTracking,
    }),
    [startTracking, stopTracking, reportError, reportErrors, removeTracking],
  );

  return (
    <MonitoringContext.Provider value={value}>
      {children}
    </MonitoringContext.Provider>
  );
}

export function useMonitoring() {
  return useContext(MonitoringContext);
}
